
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AlertRule {
  id: string;
  name: string;
  description?: string;
  ruleType: 'threshold' | 'trend' | 'anomaly' | 'pattern';
  dataSource: string;
  conditions: any;
  alertFrequency: 'immediate' | 'daily' | 'weekly';
  deliveryMethods: string[];
  isActive: boolean;
  lastTriggeredAt?: string;
}

interface ProactiveInsight {
  id: string;
  alertRuleId?: string;
  insightType: 'alert' | 'trend' | 'anomaly' | 'recommendation';
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  dataSnapshot: any;
  actionItems: string[];
  isRead: boolean;
  isDismissed: boolean;
  expiresAt?: string;
  createdAt: string;
}

export const useProactiveInsights = () => {
  const [insights, setInsights] = useState<ProactiveInsight[]>([]);
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Load existing insights
  const loadInsights = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('proactive_insights')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_dismissed', false)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error loading insights:', error);
        return;
      }

      const formattedInsights: ProactiveInsight[] = data.map(insight => ({
        id: insight.id,
        alertRuleId: insight.alert_rule_id || undefined,
        insightType: insight.insight_type as ProactiveInsight['insightType'],
        title: insight.title,
        description: insight.description,
        severity: insight.severity as ProactiveInsight['severity'],
        dataSnapshot: insight.data_snapshot || {},
        actionItems: Array.isArray(insight.action_items) ? 
          insight.action_items.filter(item => typeof item === 'string') : [],
        isRead: insight.is_read,
        isDismissed: insight.is_dismissed,
        expiresAt: insight.expires_at || undefined,
        createdAt: insight.created_at
      }));

      setInsights(formattedInsights);
      setUnreadCount(formattedInsights.filter(i => !i.isRead).length);
    } catch (error) {
      console.error('Error loading insights:', error);
    }
  }, []);

  // Load alert rules
  const loadAlertRules = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('proactive_alert_rules')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading alert rules:', error);
        return;
      }

      const formattedRules: AlertRule[] = data.map(rule => ({
        id: rule.id,
        name: rule.name,
        description: rule.description || undefined,
        ruleType: rule.rule_type as AlertRule['ruleType'],
        dataSource: rule.data_source,
        conditions: rule.conditions || {},
        alertFrequency: rule.alert_frequency as AlertRule['alertFrequency'],
        deliveryMethods: Array.isArray(rule.delivery_methods) ? 
          rule.delivery_methods.filter(method => typeof method === 'string') : ['notification'],
        isActive: rule.is_active,
        lastTriggeredAt: rule.last_triggered_at || undefined
      }));

      setAlertRules(formattedRules);
    } catch (error) {
      console.error('Error loading alert rules:', error);
    }
  }, []);

  // Create new alert rule
  const createAlertRule = async (rule: Omit<AlertRule, 'id' | 'lastTriggeredAt'>) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('proactive_alert_rules')
        .insert({
          user_id: user.id,
          name: rule.name,
          description: rule.description,
          rule_type: rule.ruleType,
          data_source: rule.dataSource,
          conditions: rule.conditions,
          alert_frequency: rule.alertFrequency,
          delivery_methods: rule.deliveryMethods,
          is_active: rule.isActive
        });

      if (error) {
        toast.error('Failed to create alert rule');
        console.error('Error creating alert rule:', error);
        return;
      }

      toast.success('Alert rule created successfully');
      await loadAlertRules();
    } catch (error) {
      console.error('Error creating alert rule:', error);
      toast.error('Failed to create alert rule');
    } finally {
      setIsLoading(false);
    }
  };

  // Mark insight as read
  const markAsRead = async (insightId: string) => {
    try {
      const { error } = await supabase
        .from('proactive_insights')
        .update({ is_read: true })
        .eq('id', insightId);

      if (error) {
        console.error('Error marking insight as read:', error);
        return;
      }

      setInsights(prev => prev.map(insight => 
        insight.id === insightId ? { ...insight, isRead: true } : insight
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking insight as read:', error);
    }
  };

  // Dismiss insight
  const dismissInsight = async (insightId: string) => {
    try {
      const { error } = await supabase
        .from('proactive_insights')
        .update({ is_dismissed: true })
        .eq('id', insightId);

      if (error) {
        console.error('Error dismissing insight:', error);
        return;
      }

      setInsights(prev => prev.filter(insight => insight.id !== insightId));
      setUnreadCount(prev => {
        const insight = insights.find(i => i.id === insightId);
        return insight && !insight.isRead ? Math.max(0, prev - 1) : prev;
      });
      toast.success('Insight dismissed');
    } catch (error) {
      console.error('Error dismissing insight:', error);
    }
  };

  // Analyze data for potential insights
  const analyzeDataForInsights = useCallback(async (data: any[], dataSource: string) => {
    if (!data || data.length === 0) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Simple trend analysis
      const numericColumns = Object.keys(data[0] || {}).filter(key => 
        typeof data[0][key] === 'number'
      );

      for (const column of numericColumns) {
        const values = data.map(row => Number(row[column])).filter(v => !isNaN(v));
        if (values.length < 3) continue;

        const recent = values.slice(-5);
        const older = values.slice(-10, -5);
        
        if (recent.length >= 3 && older.length >= 3) {
          const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
          const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
          const changePercent = ((recentAvg - olderAvg) / olderAvg) * 100;

          // Generate insight for significant changes
          if (Math.abs(changePercent) > 20) {
            const isIncrease = changePercent > 0;
            const insight = {
              insight_type: 'trend',
              title: `${isIncrease ? 'Significant Increase' : 'Significant Decrease'} Detected`,
              description: `${column} has ${isIncrease ? 'increased' : 'decreased'} by ${Math.abs(changePercent).toFixed(1)}% in recent data points. This trend may require attention.`,
              severity: Math.abs(changePercent) > 50 ? 'high' : 'medium',
              data_snapshot: {
                column,
                changePercent,
                recentAverage: recentAvg,
                previousAverage: olderAvg,
                dataSource
              },
              action_items: [
                `Investigate the cause of the ${isIncrease ? 'increase' : 'decrease'} in ${column}`,
                'Review recent business changes or external factors',
                'Consider adjusting strategies based on this trend'
              ]
            };

            // Check if similar insight already exists in the last 24 hours
            const { data: existingInsights } = await supabase
              .from('proactive_insights')
              .select('id')
              .eq('user_id', user.id)
              .eq('insight_type', 'trend')
              .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
              .ilike('title', `%${column}%`);

            if (!existingInsights || existingInsights.length === 0) {
              await supabase.from('proactive_insights').insert({
                user_id: user.id,
                ...insight
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error analyzing data for insights:', error);
    }
  }, []);

  // Monitor for anomalies
  const detectAnomalies = useCallback(async (data: any[], dataSource: string) => {
    if (!data || data.length < 10) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const numericColumns = Object.keys(data[0] || {}).filter(key => 
        typeof data[0][key] === 'number'
      );

      for (const column of numericColumns) {
        const values = data.map(row => Number(row[column])).filter(v => !isNaN(v));
        if (values.length < 10) continue;

        // Calculate basic statistics
        const mean = values.reduce((a, b) => a + b, 0) / values.length;
        const variance = values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length;
        const stdDev = Math.sqrt(variance);

        // Check for outliers (values beyond 2 standard deviations)
        const outliers = values.filter(v => Math.abs(v - mean) > 2 * stdDev);
        
        if (outliers.length > 0) {
          const maxOutlier = outliers.reduce((a, b) => Math.abs(a - mean) > Math.abs(b - mean) ? a : b);
          
          await supabase.from('proactive_insights').insert({
            user_id: user.id,
            insight_type: 'anomaly',
            title: 'Anomaly Detected',
            description: `Unusual value detected in ${column}: ${maxOutlier}. This is ${Math.abs(maxOutlier - mean).toFixed(2)} units away from the normal range.`,
            severity: Math.abs(maxOutlier - mean) > 3 * stdDev ? 'high' : 'medium',
            data_snapshot: {
              column,
              anomalousValue: maxOutlier,
              normalRange: [mean - 2 * stdDev, mean + 2 * stdDev],
              mean,
              stdDev,
              dataSource
            },
            action_items: [
              'Verify the accuracy of the anomalous data point',
              'Investigate potential causes for the unusual value',
              'Consider if this represents a new trend or one-time event'
            ]
          });
        }
      }
    } catch (error) {
      console.error('Error detecting anomalies:', error);
    }
  }, []);

  // Auto-generate recommendations based on data patterns
  const generateRecommendations = useCallback(async (data: any[], industry?: string) => {
    if (!data || data.length === 0) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Industry-specific recommendations
      const recommendations = [];

      if (industry === 'ecommerce') {
        // Check conversion rate patterns
        const conversionData = data.filter(row => typeof row.conversion_rate === 'number');
        if (conversionData.length > 0) {
          const avgConversion = conversionData.reduce((a, b) => a + b.conversion_rate, 0) / conversionData.length;
          
          if (avgConversion < 2) {
            recommendations.push({
              title: 'Low Conversion Rate Optimization',
              description: `Your average conversion rate is ${avgConversion.toFixed(2)}%, which is below industry standards. Consider optimizing your checkout process.`,
              severity: 'medium',
              actionItems: [
                'Simplify the checkout process',
                'Add customer reviews and testimonials',
                'Implement cart abandonment recovery emails',
                'A/B test product page designs'
              ]
            });
          }
        }
      }

      // Insert recommendations
      for (const rec of recommendations) {
        await supabase.from('proactive_insights').insert({
          user_id: user.id,
          insight_type: 'recommendation',
          title: rec.title,
          description: rec.description,
          severity: rec.severity,
          data_snapshot: { industry, analysis_date: new Date().toISOString() },
          action_items: rec.actionItems
        });
      }
    } catch (error) {
      console.error('Error generating recommendations:', error);
    }
  }, []);

  // Set up real-time subscription for new insights
  useEffect(() => {
    const setupSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      const channel = supabase
        .channel('proactive-insights')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'proactive_insights',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            const newInsight = payload.new as any;
            const formattedInsight: ProactiveInsight = {
              id: newInsight.id,
              alertRuleId: newInsight.alert_rule_id || undefined,
              insightType: newInsight.insight_type,
              title: newInsight.title,
              description: newInsight.description,
              severity: newInsight.severity,
              dataSnapshot: newInsight.data_snapshot || {},
              actionItems: Array.isArray(newInsight.action_items) ? 
                newInsight.action_items.filter(item => typeof item === 'string') : [],
              isRead: newInsight.is_read,
              isDismissed: newInsight.is_dismissed,
              expiresAt: newInsight.expires_at || undefined,
              createdAt: newInsight.created_at
            };

            setInsights(prev => [formattedInsight, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification for new insights
            toast.info(`New ${formattedInsight.severity} insight: ${formattedInsight.title}`, {
              action: {
                label: 'View',
                onClick: () => markAsRead(formattedInsight.id)
              }
            });
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    setupSubscription();
  }, [markAsRead]);

  // Load data on mount
  useEffect(() => {
    loadInsights();
    loadAlertRules();
  }, [loadInsights, loadAlertRules]);

  return {
    insights,
    alertRules,
    unreadCount,
    isLoading,
    createAlertRule,
    markAsRead,
    dismissInsight,
    analyzeDataForInsights,
    detectAnomalies,
    generateRecommendations,
    loadInsights,
    loadAlertRules
  };
};
